<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import MeService from '@/core/shared/me/me.service';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import DateUtils from '@/utils/date-utils';
import CalendarIconSvg from '@/assets/svg/homepage/calendar.svg';
import HpHeroBannerPng from '@/assets/png/hp-hero-banner.png';
import HpTalentSearchSvg from '@/assets/svg/homepage/hp-talent-search.svg';
import HpCandidateArchiveSvg from '@/assets/svg/homepage/hp-candidate-archive.svg';
import ProjectService from '@/core/shared/project/project.service';
import GoToCard from '@/components/Shared/Cards/GoToCard.vue';
import HeroBannerCard from '@/components/Shared/Cards/HeroBannerCard.vue';
import ActionCard from '@/components/Shared/Cards/ActionCard.vue';
import HpPerformanceDashboardSvg from '@/assets/svg/homepage/hp-performance-dashboard.svg';
import HpCreateJobTilePng from '@/assets/png/hp-createjob-tile.png';
import HpManageJobsTilePng from '@/assets/png/hp-managejobs-tile.png';
import ChevronRightIcon from '@/assets/svg/homepage/Chevron-Right-Icon.svg';
import Plus16Icon from '@/assets/svg/plus-16.svg?component';

// Services
const router = useRouter();
const meService = new MeService();
const dialogService = new DialogService();
const projectService = new ProjectService();

// State
const isCreatingJobFlag = ref(false);

// Computed properties
const userName = computed(() => meService.userProfile?.nameFirst || '');
const formattedDate = computed(() => {
  return DateUtils.formatDate(new Date(), 'dddd, MMMM D, YYYY');
});
const hasPerformanceDashboard = computed(() => meService.isEmployerAdmin);

// Methods
function openCustomReportModal() {
  dialogService.isLaborMarketReportsModalOpen = true;
}

async function handleCreateNewJob() {
  isCreatingJobFlag.value = true;
  try {
    const project = await projectService.createNewProjectAndJob();

    setTimeout(async () => {
      isCreatingJobFlag.value = false;
      await router.push(`/jobs/${project.id}/copilot-activation?isCreatingJob=true`);
    }, 500);
  } catch (error) {
    // console.error('Failed to create job:', error);
    isCreatingJobFlag.value = false;
  }
}

function navigateToJobs() {
  router.push('/jobs');
}

function navigateToPerformanceDashboard() {
  router.push('/performance-dashboard');
}
</script>

<template>
  <div class="min-h-screen" style="background: linear-gradient(0deg, #f9f9ff 50.48%, #fff 100%)">
    <div class="container mx-auto px-4 py-6">
      <!-- Welcome Header and Date Display -->
      <div class="mb-8 flex items-center justify-between">
        <h1 class="font-serif text-2xl font-black text-shade-900">Hello, {{ userName || '!' }}</h1>
        <div class="hidden items-center md:flex">
          <div
            class="flex items-center rounded-full bg-tint-20 px-4 py-2 text-sm text-shade-600 shadow-sm"
          >
            <img :src="CalendarIconSvg" alt="Calendar" class="mr-2 h-4 w-4" />
            <span>{{ formattedDate }}</span>
          </div>
        </div>
      </div>

      <!-- Labor Market & Salary Reports Hero Card -->
      <HeroBannerCard
        :imageSrc="HpHeroBannerPng"
        imageAlt="Labor Market Reports illustration"
        title="Labor Market & Salary Reports"
        :description="
          'Access unlimited Labor Market & Salary Reports with your FactoryFix subscription—combining our ' +
          'comprehensive talent data with verified internet sources for maximum accuracy. These reports ' +
          'deliver essential salary benchmarks and skill availability insights, giving you a competitive ' +
          'hiring edge at no additional cost.'
        "
        buttonText="Request a custom report"
        @buttonClick="openCustomReportModal"
      />

      <!-- Job Management Cards / "Get right to it" Section -->
      <div class="mb-8">
        <div class="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <ActionCard
            :image="HpCreateJobTilePng"
            title="Create a new job"
            description="Create a job to start receiving high-quality candidates for your hiring pipeline."
            buttonText="New job"
            :onClick="handleCreateNewJob"
            :loading="isCreatingJobFlag"
            buttonVariant="outlined"
          >
            <template #buttonIconLeft>
              <Plus16Icon class="mr-2 h-4 w-4 text-highlight-600" />
            </template>
          </ActionCard>
          <ActionCard
            :image="HpManageJobsTilePng"
            title="Manage my jobs"
            description="Manage your open roles, track hiring progress, and review candidates all in one place."
            buttonText="Manage Jobs"
            :onClick="navigateToJobs"
            buttonVariant="outlined"
          >
            <template #buttonIconRight>
              <img :src="ChevronRightIcon" alt="" class="ml-2 h-4 w-4" />
            </template>
          </ActionCard>
        </div>
      </div>

      <!-- 3-Column Card Section -->
      <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
        <GoToCard
          :icon="HpTalentSearchSvg"
          title="Search for talent"
          description="Access our pool of 11M+ quality candidates to find ideal matches for your open roles."
          buttonText="Talent Search"
          :onClick="() => router.push('/search')"
        />
        <GoToCard
          :icon="HpCandidateArchiveSvg"
          title="Search my candidate archive"
          description="Search and revisit your past candidates and previously unlocked professional profiles."
          buttonText="Candidate Archive"
          :onClick="() => router.push('/candidates')"
        />
        <GoToCard
          v-if="hasPerformanceDashboard"
          title="Monitor my performance"
          :icon="HpPerformanceDashboardSvg"
          description="Track recruitment metrics and make data-driven hiring decisions for better results."
          buttonText="Performance Dashboard"
          @click="navigateToPerformanceDashboard"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
